export type CharacterTriggerSource = 'tab_bar_plus' | 'draft_box' | 'cha_edit'
export type CharacterPlatform = 'web_pc' | 'web_h5'
export type CharacterOperationType = 'create' | 'update'
export type LegacyCharacterGender = 'Male' | 'Female' | 'Non-binary'
export type LegacyCharacterPlatform = CharacterPlatform | 'app'

// generate_image_v2 currently exposes no model selector. This stable sentinel
// identifies the backend-selected image model until the API returns its ID.
export const DEFAULT_CHARACTER_IMAGE_MODEL_ID = 'default'

export type CharacterCreateSession = {
  createSessionId: string
  triggerSource: CharacterTriggerSource
  operationType: CharacterOperationType
  regenerationIndex: number
  generationRound: number
  pageExposed: boolean
}

type TrackEvent = (
  event: string,
  properties: Record<string, unknown>,
  logType: 'CLK' | 'EXP' | 'OTHER'
) => void

type AnalyticsOptions = {
  userId?: string | null
  platform: CharacterPlatform
  trackEvent: TrackEvent
  enabled?: boolean
  contractFrozen?: boolean
  now?: () => number
}

let activeSession: CharacterCreateSession | null = null
let pendingSessionClear: ReturnType<typeof setTimeout> | null = null
let routeTransitionRetained = false
let routeTransitionTimeout: ReturnType<typeof setTimeout> | null = null

const createId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const positiveInteger = (value: unknown, allowZero = false) =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  (allowZero ? value >= 0 : value >= 1)

const isStableImageId = (value: unknown) =>
  typeof value === 'string' &&
  Boolean(value.trim()) &&
  !/^(https?:|file:|content:|data:|\/)/i.test(value)

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && Boolean(value.trim())

const styleId = (value: string | number | null | undefined) =>
  value == null ? '' : String(value)

type CharacterSuppressionReason =
  | 'feature_disabled'
  | 'contract_unfrozen'
  | 'missing_identity'
  | 'missing_session'
  | 'invalid_payload'
  | 'missing_image_id'
  | 'duplicate_event'

const suppressed = (
  reason: CharacterSuppressionReason
): CharacterTrackingResult => ({
  status: 'suppressed',
  reason,
})

type CharacterTrackingResult =
  | { status: 'sent' }
  | {
      status: 'suppressed'
      reason: CharacterSuppressionReason
    }
  | { status: 'sdk_error'; category: string }

export const normalizeCharacterTriggerSource = (
  source?: string | null
): CharacterTriggerSource | null => {
  switch (source) {
    case 'tab_bar_plus':
    case 'tab_create':
    case 'character_image':
    case null:
    case undefined:
      return 'tab_bar_plus'
    case 'draft_box':
      return 'draft_box'
    case 'cha_edit':
    case 'profile':
      return 'cha_edit'
    default:
      return null
  }
}

export const startCharacterCreateSession = (input: {
  triggerSource: CharacterTriggerSource
  operationType: CharacterOperationType
  priorCreateSessionId?: string
  forceNewSession?: boolean
}) => {
  if (pendingSessionClear) {
    clearTimeout(pendingSessionClear)
    pendingSessionClear = null
  }
  if (routeTransitionTimeout) {
    clearTimeout(routeTransitionTimeout)
    routeTransitionTimeout = null
  }
  routeTransitionRetained = false
  if (
    !input.forceNewSession &&
    activeSession?.triggerSource === input.triggerSource &&
    activeSession.operationType === input.operationType &&
    (!input.priorCreateSessionId ||
      activeSession.createSessionId === input.priorCreateSessionId)
  ) {
    return activeSession
  }

  activeSession = {
    createSessionId: input.priorCreateSessionId || createId(),
    triggerSource: input.triggerSource,
    operationType: input.operationType,
    regenerationIndex: 0,
    generationRound: 1,
    pageExposed: false,
  }
  return activeSession
}

export const getCharacterCreateSession = () => activeSession

export const clearCharacterCreateSession = () => {
  if (pendingSessionClear) {
    clearTimeout(pendingSessionClear)
    pendingSessionClear = null
  }
  if (routeTransitionTimeout) {
    clearTimeout(routeTransitionTimeout)
    routeTransitionTimeout = null
  }
  routeTransitionRetained = false
  activeSession = null
}

// The mobile image editor is a child route of the create flow. Its source and
// destination hooks unmount in separate ticks, so preserve the current session
// explicitly until the destination hook consumes it. The timeout prevents a
// failed or abandoned navigation from leaking the session into a later flow.
export const retainCharacterCreateSessionForRouteTransition = (
  timeoutMs = 30000
) => {
  routeTransitionRetained = true
  if (pendingSessionClear) {
    clearTimeout(pendingSessionClear)
    pendingSessionClear = null
  }
  if (routeTransitionTimeout) clearTimeout(routeTransitionTimeout)
  routeTransitionTimeout = setTimeout(() => {
    routeTransitionRetained = false
    routeTransitionTimeout = null
    activeSession = null
    if (pendingSessionClear) {
      clearTimeout(pendingSessionClear)
      pendingSessionClear = null
    }
  }, timeoutMs)
}

// Create details and image routes unmount during client-side transitions. A
// deferred clear lets the next route retain the same session while still
// ending abandoned flows once no create route mounts in the same commit.
export const scheduleCharacterCreateSessionClear = () => {
  if (routeTransitionRetained) return
  if (pendingSessionClear) clearTimeout(pendingSessionClear)
  pendingSessionClear = setTimeout(() => {
    activeSession = null
    pendingSessionClear = null
  }, 0)
}

export const nextCharacterRegeneration = () => {
  if (!activeSession) return null
  activeSession = {
    ...activeSession,
    regenerationIndex: activeSession.regenerationIndex + 1,
    generationRound: activeSession.generationRound + 1,
  }
  return {
    regenerateIndex: activeSession.regenerationIndex,
    generationRound: activeSession.generationRound,
    newGenerationRequestId: createId(),
  }
}

export const createCharacterAnalytics = (options: AnalyticsOptions) => {
  // The PRD contract is frozen and these events must ship by default. Tests and
  // emergency callers can still explicitly suppress the adapter.
  const enabled = options.enabled ?? true
  const contractFrozen = options.contractFrozen ?? true
  const now = options.now || Date.now

  const send = (
    event: string,
    properties: Record<string, unknown>,
    logType: 'CLK' | 'EXP'
  ) => {
    if (!enabled)
      return {
        status: 'suppressed' as const,
        reason: 'feature_disabled' as const,
      }
    if (!contractFrozen)
      return {
        status: 'suppressed' as const,
        reason: 'contract_unfrozen' as const,
      }
    if (!activeSession)
      return {
        status: 'suppressed' as const,
        reason: 'missing_session' as const,
      }
    if (!options.userId)
      return {
        status: 'suppressed' as const,
        reason: 'missing_identity' as const,
      }

    try {
      options.trackEvent(
        event,
        {
          puid: options.userId,
          server_timestamp: now(),
          create_session_id: activeSession.createSessionId,
          ...properties,
        },
        logType
      )
      return { status: 'sent' as const }
    } catch (error) {
      return {
        status: 'sdk_error' as const,
        category: error instanceof Error ? error.name : 'unknown',
      }
    }
  }

  return {
    pageExposure: () => {
      if (activeSession?.pageExposed) {
        return suppressed('duplicate_event')
      }
      const result = send(
        'character_create_page_exposure',
        {
          trigger_source: activeSession?.triggerSource,
          platform: options.platform,
        },
        'EXP'
      )
      if (result.status === 'sent' && activeSession) {
        activeSession = { ...activeSession, pageExposed: true }
      }
      return result
    },
    avatarEntryClick: () => send('character_avatar_entry_click', {}, 'CLK'),
    avatarGenerateClick: (payload: {
      modelId: string
      styleId?: string | number | null
      generateCount: number
      hasImageUpload: boolean
    }) => {
      if (
        !isNonEmptyString(payload.modelId) ||
        !positiveInteger(payload.generateCount)
      ) {
        return suppressed('invalid_payload')
      }
      return send(
        'character_avatar_generate_click',
        {
          model_id: payload.modelId,
          style_id: styleId(payload.styleId),
          generate_count: payload.generateCount,
          has_image_upload: payload.hasImageUpload ? 1 : 0,
        },
        'CLK'
      )
    },
    avatarUploadClick: () => send('character_avatar_upload_click', {}, 'CLK'),
    avatarRegenerateClick: (payload: {
      newGenerationRequestId: string
      regenerateIndex: number
      modelId: string
      styleId?: string | number | null
      generateCount: number
    }) => {
      if (
        !payload.newGenerationRequestId ||
        !isNonEmptyString(payload.modelId) ||
        !positiveInteger(payload.regenerateIndex) ||
        !positiveInteger(payload.generateCount)
      ) {
        return suppressed('invalid_payload')
      }
      return send(
        'character_avatar_regenerate_click',
        {
          new_generation_request_id: payload.newGenerationRequestId,
          regenerate_index: payload.regenerateIndex,
          model_id: payload.modelId,
          style_id: styleId(payload.styleId),
          generate_count: payload.generateCount,
        },
        'CLK'
      )
    },
    avatarSelectClick: (payload: {
      imageId: string
      imagePosition: number
      generationRound: number
    }) => {
      if (!isStableImageId(payload.imageId)) {
        return suppressed('missing_image_id')
      }
      if (
        !positiveInteger(payload.imagePosition) ||
        !positiveInteger(payload.generationRound)
      ) {
        return suppressed('invalid_payload')
      }
      return send(
        'character_avatar_select_click',
        {
          image_id: payload.imageId,
          image_position: payload.imagePosition,
          generation_round: payload.generationRound,
        },
        'CLK'
      )
    },
    draftBoxExposure: (draftCount: number) =>
      positiveInteger(draftCount, true)
        ? send(
            'character_draft_box_exposure',
            { draft_count: draftCount },
            'EXP'
          )
        : suppressed('invalid_payload'),
    saveClick: () => send('save_click', {}, 'CLK'),
    uploadCharacter: (payload: {
      operationType: CharacterOperationType
      characterFormat: 'character' | 'multi_character'
      advancedSettings: boolean
      dialogueSet: boolean
      replySet: boolean
      gender: LegacyCharacterGender
      limited: boolean
      isPublic: boolean
      voiceSet: boolean
      legacyPlatform: LegacyCharacterPlatform
      characterId: string
    }) =>
      send(
        'UploadCharacter',
        {
          operation_type: payload.operationType,
          cha_format: payload.characterFormat,
          cha_dynamic: payload.advancedSettings ? 1 : 0,
          is_dialogue_set: payload.dialogueSet ? 1 : 0,
          is_reply_set: payload.replySet ? 1 : 0,
          action_type: payload.operationType === 'update' ? 'Update' : 'Create',
          is_advanced: payload.advancedSettings,
          cha_gender: payload.gender,
          is_limited: payload.limited,
          is_public: payload.isPublic,
          is_voiceset: payload.voiceSet,
          platform: payload.legacyPlatform,
          cid: payload.characterId,
        },
        'CLK'
      ),
  }
}
